import cls from './add-domain.module.scss';
import Button from 'shared/ui/button/button';
import {
  useAddDomainMutation,
  useLazyGetUserInfoQuery,
} from 'store/api/userApiSlice';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { appRoute } from 'shared/constants/routes';
import { ICONS } from 'shared/constants/images';
import { LanguageSelect } from 'components/language-select/language-select';
import { Icon } from 'shared/ui/icon/icon';
import { Modal } from 'shared/ui/modal/modal';
import { useTranslation } from 'hooks/api/useTranslation';
import { InputField } from 'shared/ui/inputField/inputField';
import { useDebounce } from 'shared/hooks/useDebounce';
import { validateDomain } from 'shared/helpers/validateDomain';
import { snackbar } from 'shared/helpers/snackbar';
import { useCachedUserInfo, useCachedUserType } from 'hooks';
import { useSubscriptions } from 'hooks/api/useSubscriptions';
import { UserMenu } from 'components/user-menu/user-menu';
import { pushDataLayerEvent } from 'shared/helpers/dataLayer';
import { getTrafficSource } from 'utils/trafficSource';

const isStdl = process.env.REACT_APP_TYPE === 'standalone';
const START_TRIAL_EVENT_KEY = 'startTrialEventFired';

export const AddDomain = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeDomains } = useCachedUserInfo();
  const [addDomain] = useAddDomainMutation();
  const [refetchUserInfo] = useLazyGetUserInfoQuery();
  const [error, setError] = useState<string | undefined>();
  const [domain, setDomain] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [noSubModalOpen, setNoSubModalOpen] = useState(false);
  const { subscriptions } = useSubscriptions(true);

  const { isKa } = useCachedUserType();

  useEffect(() => {
    refetchUserInfo(undefined, true)
      .unwrap()
      .then(userInfo => {
        if (userInfo?.activeDomains && userInfo.activeDomains.length > 0) {
          navigate(appRoute('/dashboard'), { replace: true });
        }
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleValidate = () => {
    if (!domain) {
      setError(t('invalidDomain'));
      return;
    }
    if (!validateDomain(domain)) {
      setError(t('invalidDomain'));
    } else {
      setError(undefined);
    }
  };

  useEffect(() => {
    if (activeDomains && activeDomains.length > 0) {
      return;
    }
    try {
      // Safari (private mode / blocked storage) can throw on localStorage access
      if (localStorage.getItem(START_TRIAL_EVENT_KEY)) {
        return;
      } else {
        localStorage.setItem(START_TRIAL_EVENT_KEY, 'true');
        pushDataLayerEvent('startTrial', {
          trafficSource: getTrafficSource() ?? 'direct',
        });
      }
    } catch {
      pushDataLayerEvent('startTrial', {
        trafficSource: getTrafficSource() ?? 'direct',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const debouncedValidation = useDebounce(handleValidate, 500);

  const onChangeHandler = (value: string) => {
    setDomain(value);
    debouncedValidation();
  };

  const handleAddDomain = async () => {
    if (!domain || domain.trim() === '') {
      setError(t('invalidDomain'));
      return;
    }

    if (activeDomains && activeDomains.length > 0) {
      return;
    }

    if (subscriptions?.length === 0 && isKa) {
      setNoSubModalOpen(true);
      return;
    }

    await addDomain(domain.trim())
      .unwrap()
      .then(async r => {
        if (r.errors) {
          snackbar(r.errors.domain[0] || 'Failed to add domain');
          setError(t('invalidDomain'));
        } else {
          snackbar('Domain added successfully');
          localStorage.setItem('selectedDomain', domain.trim());
          // Wait for the invalidatesTags-triggered userInfo refetch to
          // finish before the hard nav — Safari otherwise cancels the
          // in-flight fetch, and the resulting FETCH_ERROR used to bounce
          // the user back to /auth.
          await refetchUserInfo()
            .unwrap()
            .catch(() => undefined);
          window.location.href = '/dashboard';
        }
      })
      .catch(e => {
        if (e.data?.message) {
          snackbar(e.data.message);
          setError(e.data?.message);
        } else {
          snackbar(e.data?.errors?.domain?.[0] || 'Failed to add domain');
          setError(t('invalidDomain'));
        }
      });
  };
  return (
    <div className={cls.addDomain}>
      <div className={cls.left}>
        <img src={ICONS.rivaAILogo} alt='Riva' />
        <LanguageSelect />
      </div>
      <div className={cls.content}>
        <div>
          <h2>{t('analyzeYourWebsite')}</h2>
          <p>{t('analyzeYourWebsiteDescription')}</p>
        </div>
        <div>
          <InputField
            label={t('websiteDomain')}
            value={domain}
            onChange={e => onChangeHandler(e.target.value)}
            error={!!error}
            errorMessage={error}
            placeholder={t('enterDomain')}
            name='domain'
          />
          <Button
            theme='filled'
            tone='default'
            className={cls.button}
            onClick={handleAddDomain}
          >
            {t('generateReport')}
          </Button>
        </div>
      </div>
      <div className={cls.right}>
        <UserMenu />
        <Icon
          pathToIcon={ICONS.tooltip}
          size={20}
          onClick={() => setIsModalOpen(true)}
        />
      </div>
      <div className={cls.bg}>
        <div className={cls.blue} />
        <div className={cls.pink} />
        <div className={cls.orange} />
      </div>
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        className={cls.modal}
      >
        <h2>{t('contactSupport')}</h2>
        <p>
          {t('signInSupport1')}
          <br />
          <br />
          {t('youCanContactUs')}:{' '}
          {isStdl ? (
            <a href='mailto:support@rivageo.ai'>support@rivageo.ai</a>
          ) : (
            <a href='mailto:support@xoviai.com'>support@xoviai.com</a>
          )}{' '}
          - {t('weLoveToHearFromYou')}
        </p>
        <Button
          theme='filled'
          tone='default'
          onClick={() => setIsModalOpen(false)}
        >
          {t('close')}
        </Button>
      </Modal>
      <Modal
        isOpen={noSubModalOpen}
        onClose={() => setNoSubModalOpen(false)}
        className={cls.modal}
      >
        <h2>{t('licenseSupport')}</h2>
        <p>
          {t('licenseSupport1')}
          <br />
          <br />
          {t('pleaseContactUs')}:{' '}
          {isStdl ? (
            <a href='mailto:support@rivageo.ai'>support@rivageo.ai</a>
          ) : (
            <a href='mailto:support@xoviai.com'>support@xoviai.com</a>
          )}{' '}
          {t('licenseSupport2')}
        </p>
        <Button
          theme='filled'
          tone='default'
          onClick={() => setNoSubModalOpen(false)}
        >
          {t('close')}
        </Button>
      </Modal>
    </div>
  );
};
